import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Box, Skeleton, Tab, Tabs, Typography } from "@mui/material";
import MatchCards from "./MatchCards";
import { FadeSwitch } from "./ui/motion";
import Standings from "./Standings";
import Stats from "./Stats";
import { EmptyState, PillChips } from "./ui/Pills";
import { seasonLabel, useLeagueSelection } from "../utils/leagueCatalog";

export const SECTIONS = [
  { key: "fixtures", label: "Fixtures" },
  { key: "standings", label: "Standings" },
  { key: "stats", label: "Stats" },
];

const FilterLabel = ({ children }) => (
  <Typography
    component="span"
    sx={{ fontSize: "0.68rem", fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(0,0,0,0.45)", minWidth: 64 }}
  >
    {children}
  </Typography>
);

export const LEAGUE_PHOTOS = [1, 2, 3, 4, 5, 6, 7].map((n) => `/landing/splash${n}.webp`);

const photoFor = (key) => {
  const h = [...String(key || "")].reduce((acc, ch) => (acc * 31 + ch.charCodeAt(0)) >>> 0, 7);
  return LEAGUE_PHOTOS[h % LEAGUE_PHOTOS.length];
};

/** Phones get a match photo behind the title; wider screens keep the plain heading. */
const HubHeader = ({ eyebrow, title, caption, section, sectionHref, onSectionChange, children, photoKey }) => (
  <Box component="header" sx={{ px: { xs: 0, sm: 3 }, pt: { xs: 0, sm: 3 } }}>
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        bgcolor: { xs: "#0a0a0a", sm: "transparent" },
        color: { xs: "#fff", sm: "#111" },
        px: { xs: 2, sm: 0 },
        pt: { xs: 5, sm: 0 },
        pb: { xs: 2.25, sm: 0 },
      }}
    >
      <Box sx={{ display: { xs: "block", sm: "none" } }}>
        <FadeSwitch motionKey={photoKey || title} rise={false} sx={{ position: "absolute", inset: 0 }}>
          <Box component="img" src={photoFor(photoKey || title)} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 60%", display: "block" }} />
        </FadeSwitch>
        <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.78) 100%)" }} />
      </Box>
      <Box sx={{ position: "relative" }}>
        <Typography sx={{ fontSize: "0.75rem", fontWeight: 800, letterSpacing: 2, textTransform: "uppercase", color: { xs: "rgba(255,255,255,0.75)", sm: "rgba(0,0,0,0.45)" } }}>
          {eyebrow}
        </Typography>
        <Typography
          component="h1"
          sx={{ fontSize: { xs: "1.9rem", sm: "2.5rem" }, fontWeight: 900, letterSpacing: "-0.03em", lineHeight: 1.1, mt: 0.25, textShadow: { xs: "0 2px 12px rgba(0,0,0,0.35)", sm: "none" } }}
        >
          {title}
        </Typography>
        {caption && <Typography sx={{ color: { xs: "rgba(255,255,255,0.75)", sm: "rgba(0,0,0,0.55)" }, fontSize: "0.85rem", mt: 0.5 }}>{caption}</Typography>}
      </Box>
    </Box>
    <Box sx={{ px: { xs: 2, sm: 0 } }}>
    {children}
    <Tabs
      value={SECTIONS.findIndex((s) => s.key === section)}
      onChange={onSectionChange ? (_, i) => onSectionChange(SECTIONS[i].key) : undefined}
      sx={{
        mt: 2,
        minHeight: 40,
        borderBottom: "1px solid rgba(0,0,0,0.08)",
        "& .MuiTab-root": { textTransform: "none", fontWeight: 800, fontSize: "0.95rem", minHeight: 40, px: 0, mr: 3, minWidth: 0, color: "rgba(0,0,0,0.5)" },
        "& .Mui-selected": { color: "#111 !important" },
        "& .MuiTabs-indicator": { bgcolor: "#111", height: 3, borderRadius: 2 },
      }}
    >
      {SECTIONS.map((s) =>
        sectionHref ? (
          <Tab key={s.key} label={s.label} component={Link} to={sectionHref(s.key)} replace />
        ) : (
          <Tab key={s.key} label={s.label} />
        )
      )}
    </Tabs>
    </Box>
  </Box>
);

const SectionContent = ({ section, ...props }) => {
  if (section === "standings") return <Standings {...props} showTitle={false} />;
  if (section === "stats") return <Stats {...props} showTitle={false} />;
  return <MatchCards {...props} />;
};

const SectionBody = (props) => (
  <FadeSwitch motionKey={[props.section, props.city, props.division, props.season].join("|")}>
    <SectionContent {...props} />
  </FadeSwitch>
);

const shell = (children) => <Box sx={{ maxWidth: 1200, mx: "auto", minHeight: "70vh", pb: 4 }}>{children}</Box>;

/** Youth: a single league with no city / division choice. */
export const YouthHub = ({ section, onSectionChange }) =>
  shell(
    <>
      <HubHeader
        eyebrow="HSL Youth"
        title="Youth league"
        caption="3 pts per win, 1 per draw, from completed game scores."
        section={section}
        onSectionChange={onSectionChange}
      />
      <SectionBody section={section} youthMode />
    </>
  );

/**
 * Fixtures / standings / stats for one sport. City, division and season options come
 * from published fixtures; the choice lives in the URL and is remembered between visits.
 */
const LeagueHub = ({ sport = "Soccer", label = "HSL", caption, section, sectionHref, onSectionChange }) => {
  const sel = useLeagueSelection(sport);
  const season = sel.season ? seasonLabel(sel.season) : null;

  if (sel.loading) {
    return shell(
      <Box sx={{ px: { xs: 2, sm: 3 }, pt: 3 }}>
        <Skeleton width={120} />
        <Skeleton width={260} height={48} />
        <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rounded" width={96} height={32} sx={{ borderRadius: 999 }} />
          ))}
        </Box>
        <Skeleton variant="rounded" height={260} sx={{ mt: 4, borderRadius: 3 }} />
      </Box>
    );
  }

  if (!sel.city) {
    return shell(
      <>
        <HubHeader eyebrow={label} title="No games yet" section={section} sectionHref={sectionHref} onSectionChange={onSectionChange} />
        <EmptyState
          title="This season's schedule isn't out yet"
          body="Fixtures, standings and stats show up here once games are published. Spots for the next season are open now."
          action={{ label: "Register for a league", to: "/register/select-division" }}
        />
      </>
    );
  }

  return shell(
    <>
      <HubHeader
        eyebrow={[label, season].filter(Boolean).join(" · ")}
        title={`${sel.city} · ${sel.division}`}
        photoKey={`${sel.city}|${sel.division}`}
        caption={caption}
        section={section}
        sectionHref={sectionHref}
        onSectionChange={onSectionChange}
      >
        <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1.25 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            <FilterLabel>City</FilterLabel>
            <PillChips
              ariaLabel="City"
              value={sel.city}
              onChange={sel.setCity}
              options={sel.catalog.map((c) => ({ value: c.city, label: c.city }))}
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            <FilterLabel>Division</FilterLabel>
            <PillChips
              ariaLabel="Division"
              value={sel.division}
              onChange={sel.setDivision}
              options={sel.divisions.map((d) => ({ value: d.division, label: d.division }))}
            />
          </Box>
          {sel.seasons.length > 1 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
              <FilterLabel>Season</FilterLabel>
              <PillChips
                ariaLabel="Season"
                value={sel.season}
                onChange={sel.setSeason}
                options={sel.seasons.map((s) => ({ value: s, label: seasonLabel(s) }))}
              />
            </Box>
          )}
        </Box>
      </HubHeader>
      <SectionBody
        section={section}
        city={sel.city}
        division={sel.division}
        sport={sport}
        ageGroup={sel.ageGroup || "Adult"}
        season={sel.season}
        computeFromFixtures
      />
    </>
  );
};

const PATHS = { fixtures: "/schedule", standings: "/standings", stats: "/stats" };

/** Main soccer league at /schedule, /standings and /stats; switching tabs keeps the filters. */
export const LeagueRoute = ({ section }) => {
  const { search } = useLocation();
  const keep = new URLSearchParams(search);
  keep.delete("date");
  const qs = keep.toString();
  return <LeagueHub section={section} sectionHref={(k) => `${PATHS[k]}${qs ? `?${qs}` : ""}`} />;
};

export default LeagueHub;
